import * as React from 'react'
import PropTypes from 'prop-types'
import { ApolloProvider } from '@apollo/client'
import { hot } from 'react-hot-loader'
import 'isomorphic-fetch'

import '../../utils/lifecycle'
import * as sentry from '../../utils/sentry'
import { getSpaVersion } from '../../utils/spa-version'
import { getApolloClient } from '../../apollo/apollo-client'
import { DevApp } from './DevApp'
import { OnlineOrderingApp } from './OnlineOrderingApp'
import { MultiLocationPickerApp } from './MultiLocationPickerApp'
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary'
import { OOGlobalsProvider } from '@local/do-secundo-oo-globals-provider'
import { AuthProvider } from '../AuthProvider/AuthProvider'
import * as heapAnalytics from '../../third-party/HeapAnalytics'
import { lazyInitAuthClient } from '@toasttab/guest-authentication-js'

sentry.init({
  publicKey: '33b889f63e6643cd9e970b491d574d5a',
  projectId: '1319042'
})

// used to proxy analytics for cornucopia transition
window.toastAnalytics = heapAnalytics
heapAnalytics.load(() => {
  console.log('Heap loaded')
})

export const AppComponent = ({
  location = window.location,
  ooGlobals = window.OO_GLOBALS || {
    devError: {
      message: 'Could not find OO_GLOBALS.'
    },
    enabledFeatureFlags: []
  }
}) => {
  const useDevApp = !(
    ooGlobals.routerBasename &&
    location.pathname.startsWith(ooGlobals.routerBasename)
  )

  const isUsingMLP = ooGlobals.mode === 'MLP'
  const spaVersion = getSpaVersion()
  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [authClient] = React.useState(() =>
    lazyInitAuthClient({
      // called on user login refresh
      whenUserSessionHasResumed: () => setIsAuthenticated(true)
    })
  )

  return (
    <ErrorBoundary>
      <ApolloProvider client={getApolloClient(authClient)}>
        {useDevApp ? (
          <DevApp />
        ) : (
          <OOGlobalsProvider
            devError={ooGlobals.devError}
            enabledFeatureFlags={ooGlobals.enabledFeatureFlags}
            gatewayBaseUri={ooGlobals.gatewayBaseUri}
            outpostIdentifier={ooGlobals.outpostIdentifier}
            toastwebBaseUri={ooGlobals.toastwebBaseUri}
            restaurantGuid={ooGlobals.restaurantGuid}
            routerBasename={ooGlobals.routerBasename}
            shortUrl={ooGlobals.shortUrl}
            spaVersion={spaVersion.current}
            spaPreviousVersion={spaVersion.previous}
            mode={ooGlobals.mode}
            managementGroupGuid={ooGlobals.managementGroupGuid}
            mapboxAccessToken={ooGlobals.mapboxAccessToken}
            toastTakeoutCTAEnabled={Boolean(ooGlobals.toastTakeoutCTAEnabled)}
          >
            <AuthProvider
              authClient={authClient}
              setIsAuthenticated={setIsAuthenticated}
              isAuthenticated={isAuthenticated}
            >
              {isUsingMLP ? <MultiLocationPickerApp /> : <OnlineOrderingApp />}
            </AuthProvider>
          </OOGlobalsProvider>
        )}
      </ApolloProvider>
    </ErrorBoundary>
  )
}

AppComponent.propTypes = {
  // Intended to be window.location
  location: PropTypes.shape({
    pathname: PropTypes.any.isRequired
  }),
  ooGlobals: PropTypes.object
}

export const App = hot(module)(AppComponent)
